import {BoxAccess, PendingView, useAsync, useBoxes, useOccupancies, usePlatform} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo} from "react";
import {CloseableMessage} from "./CloseableMessage";
import {ClearingStatus, ClesyBoxType, CubeOrder, OrderStatus} from "./model";
import {useLocalization} from "../i18n";

interface OrderFlowProps {
    order: CubeOrder;
    onDone: () => void;
}

export function OrderFlow({order, onDone}: OrderFlowProps) {
    const {t} = useLocalization();
    switch (order.orderStatus) {
        case OrderStatus.Ordered:
            return <OrderStore order={order} onDone={onDone} />;

        case OrderStatus.WaitingReturn:
            if (order.clearingStatus == ClearingStatus.MonthlyInvoice || order.clearingStatus == ClearingStatus.Success) {
                return <OrderPickup order={order} onDone={onDone}/>;
            }
            else {
                return (
                    <CloseableMessage message={t("OrderPaymentRequired")} onClose={onDone} />
                );
            }

        case OrderStatus.Completed:
            return (
                <CloseableMessage message={t("OrderCompleted")} onClose={onDone}/>
            );

        case OrderStatus.WaitingPickup:
        case OrderStatus.Cleaning:
            return (
                <CloseableMessage message={t("OrderIsCleaning")} onClose={onDone}/>
            );
    }
}


interface OrderStoreProps {
    order: CubeOrder;
    onDone: () => void;
}

export function OrderStore({order, onDone}: OrderStoreProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const boxes = useBoxes();

    const occupy = useAsync(() => platform.occupy({
        type: ClesyBoxType.SuitBag,
        accessCode: order.accessCode,
        content: { order },
    }), [platform, order]);

    const box = useMemo(() => boxes.find(box => box.number == occupy.result?.boxNumber), [boxes, occupy.result]);

    const handleConfirm = useCallback(async () => {
        await platform.confirmOccupancy(occupy.result!.uuid);
        onDone();
    }, [platform, occupy.result, onDone]);

    const handleCancel = useCallback(async () => {
        await platform.cancelOccupancy(occupy.result!.uuid);
        onDone();
    }, [platform, occupy.result, onDone]);

    if (occupy.error) {
        return (
            <CloseableMessage message={t("OrderStoreError")} onClose={onDone} />
        );
    }
    else if (box) {
        return (
            <BoxAccess
                boxes={box}
                platform={platform}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                autoConfirm={60}
                boxOpenTitle={t("OrderStoreOpenBoxTitle")}
                boxOpenMessage={t("OrderStoreOpenBoxMessage", {boxNames: box.number})}
                confirmTitle={t("StoreConfirmTitle")}
                confirmMessage={t("OrderStoreConfirmMessage", {boxNames: box.number})}
                strings={t}
            />
        )
    }
    return <PendingView message={t("OccupancyPending")}/>
}



interface OrderPickupProps {
    order: CubeOrder;
    onDone: () => void;
}

function OrderPickup({order, onDone}: OrderPickupProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const occupancies = useOccupancies();
    const boxes = useBoxes();
    const occupancy = useMemo(() => occupancies.find(occupancy => occupancy.accessCode == order.accessCode), [occupancies, order]);
    const box = useMemo(() => boxes.find(box => box.number == occupancy?.boxNumber), [boxes, occupancy]);

    const handleConfirm = useCallback(async () => {
        try {
            await platform.endOccupancy(occupancy!.uuid);
        }
        catch (error) {
            console.error("Error while ending occupancy", error);
        }
        onDone();
    }, []);

    if (box) {
        return (
            <BoxAccess
                boxes={box}
                platform={platform}
                onCancel={onDone}
                onConfirm={handleConfirm}
                autoConfirm={60}
                timeout
                boxOpenTitle={t("OrderPickupOpenBoxTitle")}
                boxOpenMessage={t("OrderPickupOpenBoxMessage", {boxNames: box.number})}
                confirmTitle={t("OrderPickupConfirmTitle")}
                confirmMessage={t("OrderPickupConfirmMessage", {boxNames: box.number})}
                strings={t}
            />
        )
    }
    else {
        return <PendingView message={t("PickupPending")}/>
    }
}
